import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, Loader2, CheckCheck, X, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import Button from '../components/ui/Button';
import { API_PATH, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import { formatDate } from '../utils/formatDate';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import styles from './TransferHistoryPage.module.css';

const API_URL = `${API_PATH}/events`;

const StatusBadge = ({ status }) => {
    const variant = status === 'pending' ? 'warning' : status === 'accepted' ? 'success' : 'neutral';
    return (
        <span className={`${styles.statusBadge} ${styles[`status_${status}`]}`}>
            {status}
        </span>
    );
};

const TransferHistoryPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [incoming, setIncoming] = useState([]);
    const [outgoing, setOutgoing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acceptingId, setAcceptingId] = useState(null);
    const [decliningId, setDecliningId] = useState(null);
    const [cancelingId, setCancelingId] = useState(null);
    const [toast, setToast] = useState('');

    const canCreateEvents = user?.canCreateEvents !== false;

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await request(`${API_URL}/transfer-requests`);
            if (res.ok) {
                const data = await res.json();
                setIncoming(data.incoming || []);
                setOutgoing(data.outgoing || []);
            }
        } catch (err) {
            console.error('Failed to fetch transfer requests', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!canCreateEvents) {
            navigate('/dashboard', { replace: true });
            return;
        }
        fetchRequests();
    }, [canCreateEvents, navigate, fetchRequests]);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(''), 2500);
        return () => clearTimeout(t);
    }, [toast]);

    const handleAccept = async (eventId) => {
        setAcceptingId(eventId);
        try {
            const res = await request(`${API_URL}/${eventId}/transfer/accept`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setToast('Event transferred to you.');
                fetchRequests();
            } else {
                setToast(data.error || 'Could not accept.');
            }
        } catch (err) {
            setToast('Could not accept transfer.');
        } finally {
            setAcceptingId(null);
        }
    };

    const handleDecline = async (eventId) => {
        setDecliningId(eventId);
        try {
            const res = await request(`${API_URL}/${eventId}/transfer/decline`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setToast('Transfer request declined.');
                fetchRequests();
            } else {
                setToast(data.error || 'Could not decline.');
            }
        } catch (err) {
            setToast('Could not decline.');
        } finally {
            setDecliningId(null);
        }
    };

    const handleCancel = async (eventId) => {
        setCancelingId(eventId);
        try {
            const res = await request(`${API_URL}/${eventId}/transfer/cancel`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setToast('Transfer request cancelled. You can send again to the correct email.');
                fetchRequests();
            } else {
                setToast(data.error || 'Could not cancel.');
            }
        } catch (err) {
            setToast('Could not cancel request.');
        } finally {
            setCancelingId(null);
        }
    };

    if (!canCreateEvents) return null;

    return (
        <div className={styles.page}>
            {toast && (
                <div className={styles.toast} role="status">
                    <CheckCheck size={14} /> {toast}
                </div>
            )}

            <PageHeroHeader
                icon={History}
                title="Transfer History"
                subtitle="View and manage event transfer requests. Accept incoming transfers or see status of outgoing ones."
            />

            {loading ? (
                <div className={styles.loading}>
                    <Loader2 size={28} className={styles.spinner} />
                    <span>Loading transfer requests…</span>
                </div>
            ) : (
                <div className={styles.sections}>
                    {/* Incoming */}
                    <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>
                            <ArrowDownLeft size={18} /> Incoming
                        </h2>
                        {incoming.length === 0 ? (
                            <p className={styles.empty}>No incoming transfer requests.</p>
                        ) : (
                            <ul className={styles.list}>
                                {incoming.map((r) => (
                                    <li key={r.id} className={styles.item}>
                                        <div className={styles.itemMain}>
                                            <strong className={styles.eventName}>{r.event_name}</strong>
                                            <span className={styles.meta}>
                                                From {r.from_user_name} ({r.from_user_email})
                                            </span>
                                            <span className={styles.date}>{formatDate(r.created_at) || '—'}</span>
                                        </div>
                                        <div className={styles.itemActions}>
                                            <StatusBadge status={r.status} />
                                            {r.status === 'pending' && (
                                                <div className={styles.buttons}>
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        className={styles.btnDecline}
                                                        onClick={() => handleDecline(r.event_id)}
                                                        disabled={decliningId === r.event_id}
                                                    >
                                                        {decliningId === r.event_id ? <Loader2 size={14} className={styles.spin} /> : <X size={14} />}
                                                        Decline
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        className={styles.btnAccept}
                                                        onClick={() => handleAccept(r.event_id)}
                                                        disabled={acceptingId === r.event_id}
                                                        icon={acceptingId === r.event_id ? <Loader2 size={14} className={styles.spin} /> : <CheckCheck size={14} />}
                                                    >
                                                        {acceptingId === r.event_id ? 'Accepting…' : 'Accept'}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Outgoing */}
                    <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>
                            <ArrowUpRight size={18} /> Outgoing
                        </h2>
                        {outgoing.length === 0 ? (
                            <p className={styles.empty}>No outgoing transfer requests.</p>
                        ) : (
                            <ul className={styles.list}>
                                {outgoing.map((r) => (
                                    <li key={r.id} className={styles.item}>
                                        <div className={styles.itemMain}>
                                            <strong className={styles.eventName}>{r.event_name}</strong>
                                            <span className={styles.meta}>
                                                To {r.to_user_name} ({r.to_user_email})
                                            </span>
                                            <span className={styles.date}>{formatDate(r.created_at) || '—'}</span>
                                        </div>
                                        <div className={styles.itemActions}>
                                            <StatusBadge status={r.status} />
                                            {r.status === 'pending' && (
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    className={styles.btnDecline}
                                                    onClick={() => handleCancel(r.event_id)}
                                                    disabled={cancelingId === r.event_id}
                                                >
                                                    {cancelingId === r.event_id ? <Loader2 size={14} className={styles.spin} /> : <X size={14} />}
                                                    Cancel request
                                                </Button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};

export default TransferHistoryPage;
